import { ICONS } from "../../../../_helpers/icons.constants";

/**
 * Icon colours for a row-action button.
 *
 * Each value is a class whose only job in `_ag-grid.scss` is to apply one icon colour
 * TOKEN - `ERROR` paints `var(--icon-error)`. No colour is spelled out here or there,
 * so a new variant means adding a token to the `$--icon-button-colors` map in that file
 * and a key here, and light/dark come for free with the theme.
 */
export const ICON_BUTTON_COLOR = {
  /** `--icon-primary` - body-text black, for actions that carry no status. */
  NEUTRAL: '--is-neutral',
  /** `--icon-secondary` - a supporting action that should not compete with the row. */
  SECONDARY: '--is-secondary',
  /** `--icon-info` - the blue used for edit and other "open this" actions. */
  INFO: '--is-info',
  SUCCESS: '--is-success',
  WARNING: '--is-warning',
  ERROR: '--is-error',
  DISABLED: '--is-disabled',
} as const;

/**
 * Where an action sits inside the cell. Everything a column adds stays in the LEFT
 * group in declared order; `edit` and `delete` carry heavier weights so they are always
 * the last two icons - delete rightmost - whatever order the column lists them in.
 *
 * The gap between LEADING and EDIT is deliberate: a future action that has to sit
 * between the two groups can be given a weight without renumbering these.
 */
export const ICON_BUTTON_ORDER = {
  LEADING: 0,
  EDIT: 90,
  DELETE: 100,
} as const;

export interface IconButtonDefaults {
  buttonIcon: string;
  buttonClass: string;
  /** Left out means `LEADING`: left group, declared order. */
  order?: number;
}

/**
 * The renderer's own icon set, keyed by `userAction`.
 *
 * A column names the action and gets the icon, the colour and the position from here -
 * it passes no `buttonIcon` and no `buttonClass` at all, which is what stops the same
 * action drifting to a different icon or colour from one grid to the next (edit was
 * blue on three screens and green on two before this map existed).
 *
 * Two things to know when adding a column:
 *
 * - **The action name has to be truthful.** A button that opens a log has to say
 *   `log`, not `delete`, or it inherits the trash icon.
 * - **A new action belongs in this map**, not in the column. `buttonIcon` /
 *   `buttonClass` are still honoured as a per-column override for a genuine one-off,
 *   but reaching for them is the signal that an entry is missing here.
 */
export const ICON_BUTTON_DEFAULTS: Readonly<Record<string, IconButtonDefaults>> = {
  // --- Right group: pinned in this order at the end of every action cell. ---
  edit: {
    buttonIcon: ICONS.EDIT_INLINE,
    buttonClass: ICON_BUTTON_COLOR.INFO,
    order: ICON_BUTTON_ORDER.EDIT,
  },
  delete: {
    buttonIcon: ICONS.TRASH,
    buttonClass: ICON_BUTTON_COLOR.ERROR,
    order: ICON_BUTTON_ORDER.DELETE,
  },
  // `deleted` is the same button under an older action name still live in a few grids.
  deleted: {
    buttonIcon: ICONS.TRASH,
    buttonClass: ICON_BUTTON_COLOR.ERROR,
    order: ICON_BUTTON_ORDER.DELETE,
  },

  // --- Left group: drawn in the order the column declares them. ---
  view: { buttonIcon: ICONS.EYE, buttonClass: ICON_BUTTON_COLOR.NEUTRAL },
  review: { buttonIcon: ICONS.EYE, buttonClass: ICON_BUTTON_COLOR.NEUTRAL },
  log: { buttonIcon: ICONS.ICON_LOG, buttonClass: ICON_BUTTON_COLOR.INFO },
  logForDocument: { buttonIcon: ICONS.ICON_LOG, buttonClass: ICON_BUTTON_COLOR.INFO },
  recall: { buttonIcon: ICONS.ROTATE_CCW, buttonClass: ICON_BUTTON_COLOR.WARNING },
  restore: { buttonIcon: ICONS.HISTORY, buttonClass: ICON_BUTTON_COLOR.NEUTRAL },
  add: { buttonIcon: ICONS.PLUS, buttonClass: ICON_BUTTON_COLOR.INFO },
  copy: { buttonIcon: ICONS.COPY, buttonClass: ICON_BUTTON_COLOR.NEUTRAL },
  download: { buttonIcon: ICONS.DOWNLOAD, buttonClass: ICON_BUTTON_COLOR.NEUTRAL },
  share: { buttonIcon: ICONS.SHARE, buttonClass: ICON_BUTTON_COLOR.INFO },
  approve: { buttonIcon: ICONS.ICON_TICK, buttonClass: ICON_BUTTON_COLOR.SUCCESS },
  reject: { buttonIcon: ICONS.X_CIRCLE, buttonClass: ICON_BUTTON_COLOR.ERROR },
  assign: { buttonIcon: ICONS.USER_PLUS, buttonClass: ICON_BUTTON_COLOR.INFO },
  reAssigned: { buttonIcon: ICONS.USER_CHECK, buttonClass: ICON_BUTTON_COLOR.INFO },
};
